/**
 * Регистр ФИО и наименований должностей (names-and-positions-normalization, решение заказчицы
 * 15.10.2026): «в ФИО — каждое новое слово с большой буквы, должность — с большой буквы».
 * Зеркало серверных правил `backend/internal/domain/name_case.go` (те же примеры в тестах): поле
 * показывает итог на уходе с поля, а сервер всё равно приводит значение сам.
 */

/** Тюркские частицы отчества пишутся со строчной: «Мамедов Ильхам Гейдар оглы». */
const PATRONYMIC_PARTICLES = new Set(['оглы', 'кызы', 'улы', 'уулу', 'гызы'])

const isUpper = (ch: string) => ch !== ch.toLowerCase() && ch === ch.toUpperCase()
const isLower = (ch: string) => ch !== ch.toUpperCase() && ch === ch.toLowerCase()

/**
 * Часть слова: если буквы после первой одного регистра («иванов», «ИВАНОВ», «иВАНОВ») —
 * «Иванов»; смешанного («МакКартни») — только первая буква заглавная.
 */
function capitalizeSegment(seg: string): string {
  const chars = Array.from(seg)
  if (chars.length === 0) return seg
  const [first, ...restChars] = chars
  const rest = restChars.join('')
  const mixed = restChars.some(isUpper) && restChars.some(isLower)
  return first.toUpperCase() + (mixed ? rest : rest.toLowerCase())
}

/** Каждая часть слова между дефисами и точками — с большой; частица после дефиса — строчная. */
function capitalizeSegments(word: string): string {
  const parts = word.split(/([-.])/)
  return parts
    .map((part, i) => {
      if (part === '-' || part === '.' || part === '') return part
      if (i > 0 && PATRONYMIC_PARTICLES.has(part.toLowerCase())) return part.toLowerCase()
      return capitalizeSegment(part)
    })
    .join('')
}

/**
 * ФИО → «Иванов Иван Иванович»: пробелы по краям сняты, внутри схлопнуты; каждое слово, каждая
 * часть через дефис и буква после точки (инициалы) — с большой; смешанный регистр сохраняется,
 * кроме первой буквы; «оглы», «кызы», «улы», «уулу», «гызы» (не первым словом) — строчные.
 */
export function normalizePersonName(s: string): string {
  return s
    .split(/\s+/)
    .filter(Boolean)
    .map((w, i) => (i > 0 && PATRONYMIC_PARTICLES.has(w.toLowerCase()) ? w.toLowerCase() : capitalizeSegments(w)))
    .join(' ')
}

/** Как `domain.CleanPositionName`: пробелы, дефис без пробелов, без точки в конце. */
export function cleanPositionName(s: string): string {
  return s
    .split(/\s+/)
    .filter(Boolean)
    .join(' ')
    .replace(/\s*-\s*/g, '-')
    .replace(/\.+$/, '')
    .trim()
}

/** Служебные слова, которые в наименовании ЗАГЛАВНЫМИ пишутся строчными (прочие короткие — аббревиатуры). */
const POSITION_FUNCTION_WORDS = new Set([
  'по', 'в', 'во', 'и', 'с', 'со', 'на', 'для', 'из', 'при', 'без', 'к', 'ко', 'о', 'об', 'до', 'за',
  'над', 'под', 'или', 'у',
])

/** В строке нет строчных букв и есть слово из 4+ букв (иначе это аббревиатура целиком: «ГИП»). */
function isAllCaps(s: string): boolean {
  if (Array.from(s).some(isLower)) return false
  return /\p{L}{4,}/u.test(s)
}

function lowerAllCapsPosition(s: string): string {
  return s.replace(/\p{L}+/gu, (w) => {
    const lw = w.toLowerCase()
    return Array.from(w).length >= 4 || POSITION_FUNCTION_WORDS.has(lw) ? lw : w
  })
}

/**
 * Должность → очищенная, первая буква заглавная, остальное как введено («Инженер АСУ ТП»,
 * «ИТ-специалист»). Наименование целиком ЗАГЛАВНЫМИ («ГЕНЕРАЛЬНЫЙ ДИРЕКТОР») — обычным регистром,
 * короткие слова-аббревиатуры остаются. Служебные записи в скобках не меняются.
 */
export function normalizePositionName(s: string): string {
  let out = cleanPositionName(s)
  if (!out || out.startsWith('(')) return out
  if (isAllCaps(out)) out = lowerAllCapsPosition(out)
  const [first, ...rest] = Array.from(out)
  return first.toUpperCase() + rest.join('')
}

